import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors, styles } from '../styles/styles';

const TimelineView = ({ thoughts = [] }) => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekDates, setWeekDates] = useState([]);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swiping, setSwiping] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const contentRef = useRef(null);
  const prevDayContentRef = useRef(null);
  const nextDayContentRef = useRef(null);
  const containerRef = useRef(null);

  // Minimum swipe distance (px)
  const minSwipeDistance = 50;
  // Add minimum vertical distance to detect scrolling vs swiping
  const minVerticalScrollDistance = 10;
  // Track vertical touch position
  const [touchStartY, setTouchStartY] = useState(null);

  // Update container width when resizing or on initial load
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        setContainerWidth(width);
      }
    };
    
    // Initial measurement
    updateContainerWidth();
    
    // Listen for resize events
    window.addEventListener('resize', updateContainerWidth);
    
    return () => {
      window.removeEventListener('resize', updateContainerWidth);
    };
  }, []);

  // Handle touch start
  const onTouchStart = (e) => {
    // Don't prevent default - allow normal scrolling
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setTouchStartY(e.targetTouches[0].clientY);
    setSwiping(false); // Start with swiping off
    setSwipeOffset(0);
  };

  // Handle touch move
  const onTouchMove = (e) => {
    const touchY = e.targetTouches[0].clientY;
    const touchX = e.targetTouches[0].clientX;
    
    // If we haven't determined if swiping yet
    if (touchStart && touchStartY && !swiping) {
      const deltaX = Math.abs(touchX - touchStart);
      const deltaY = Math.abs(touchY - touchStartY);
      
      // If movement is more vertical than horizontal, let browser handle it
      if (deltaY > deltaX || deltaY > minVerticalScrollDistance) {
        return; // Allow default vertical scrolling
      }
      
      // If clear horizontal movement, start swiping mode
      if (deltaX > minVerticalScrollDistance) {
        setSwiping(true);
      }
    }
    
    // Only handle swipe if we're in swiping mode
    if (swiping) {
      // Prevent default only when we're actually swiping horizontally
      e.preventDefault();
      setTouchEnd(touchX);
      
      const currentOffset = touchX - touchStart;
      
      // Apply resistance as you get further from center
      const resistance = containerWidth > 0 ? (Math.abs(currentOffset) / containerWidth) * 0.5 : 0.5;
      const resistedOffset = currentOffset * (1 - resistance);
      
      setSwipeOffset(resistedOffset);
    }
  };

  // Handle touch end
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !swiping) {
      setSwiping(false);
      setSwipeOffset(0);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    const transitionDuration = 300; // ms
    
    if (isLeftSwipe) {
      // First animate the swipe to completion
      document.querySelectorAll('.calendar-page').forEach(el => {
        el.style.transition = `transform ${transitionDuration}ms cubic-bezier(0.165, 0.84, 0.44, 1), opacity ${transitionDuration}ms ease-out`;
      });
      
      // Move everything one full screen left
      if (prevDayContentRef.current) prevDayContentRef.current.style.transform = 'translateX(-200%)';
      if (contentRef.current) contentRef.current.style.transform = 'translateX(-100%)';
      if (nextDayContentRef.current) nextDayContentRef.current.style.transform = 'translateX(0)';
      
      // After the animation completes, update the date and reset positions
      setTimeout(() => {
        const nextDayDate = new Date(currentDate);
        nextDayDate.setDate(nextDayDate.getDate() + 1);
        
        // Remove transitions to prevent visible reset
        document.querySelectorAll('.calendar-page').forEach(el => {
          el.style.transition = 'none';
        });
        
        // Update state
        setCurrentDate(nextDayDate);
        setSwiping(false);
        setSwipeOffset(0);
      }, transitionDuration);
    } else if (isRightSwipe) {
      // First animate the swipe to completion
      document.querySelectorAll('.calendar-page').forEach(el => {
        el.style.transition = `transform ${transitionDuration}ms cubic-bezier(0.165, 0.84, 0.44, 1), opacity ${transitionDuration}ms ease-out`;
      });
      
      // Move everything one full screen right
      if (prevDayContentRef.current) prevDayContentRef.current.style.transform = 'translateX(0)';
      if (contentRef.current) contentRef.current.style.transform = 'translateX(100%)';
      if (nextDayContentRef.current) nextDayContentRef.current.style.transform = 'translateX(200%)';
      
      // After the animation completes, update the date and reset positions
      setTimeout(() => {
        const prevDayDate = new Date(currentDate);
        prevDayDate.setDate(prevDayDate.getDate() - 1);
        
        // Remove transitions to prevent visible reset
        document.querySelectorAll('.calendar-page').forEach(el => {
          el.style.transition = 'none';
        });
        
        // Update state
        setCurrentDate(prevDayDate);
        setSwiping(false);
        setSwipeOffset(0);
      }, transitionDuration);
    } else {
      // Not enough swipe distance, bounce back with transition
      document.querySelectorAll('.calendar-page').forEach(el => {
        el.style.transition = `transform ${transitionDuration}ms cubic-bezier(0.215, 0.61, 0.355, 1), opacity ${transitionDuration}ms ease-out`;
      });
      
      setSwipeOffset(0);
      
      setTimeout(() => {
        document.querySelectorAll('.calendar-page').forEach(el => {
          el.style.transition = 'none';
        });
        
        setSwiping(false);
      }, transitionDuration);
    }
  };

  // Generate date array for date selector
  useEffect(() => {
    // Get dates for the current week (Sunday to Saturday)
    const dates = [];
    const tempDate = new Date(currentDate);
    
    // Calculate the previous Sunday (start of week)
    const day = tempDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
    tempDate.setDate(tempDate.getDate() - day); // Go back to Sunday
    
    // Generate 7 days (Sunday through Saturday)
    for (let i = 0; i < 7; i++) {
      dates.push(new Date(tempDate));
      tempDate.setDate(tempDate.getDate() + 1);
    }
    
    setWeekDates(dates);
  }, [currentDate]);

  // Format date for the date selector
  const formatDateShort = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      day: 'numeric'
    }).format(date);
  };

  // Format month and year
  const formatMonthYear = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  // Format day for the date selector
  const formatDay = (date) => {
    const day = new Intl.DateTimeFormat('en-US', {
      weekday: 'long'
    }).format(date);
    
    // Return just the first letter of the day name
    return day.charAt(0);
  };

  // Filter thoughts for selected day
  useEffect(() => {
    if (!thoughts || !thoughts.length) {
      return;
    }

    const dayStart = new Date(currentDate);
    dayStart.setHours(0, 0, 0, 0);
    
    const dayEnd = new Date(currentDate);
    dayEnd.setHours(23, 59, 59, 999);
    
    // Filter thoughts for this day
    const filteredThoughts = thoughts.filter(thought => {
      // Skip thoughts without timestamps
      if (!thought || !thought.timestamp) return false;
      
      try {
        const thoughtDate = new Date(thought.timestamp);
        return thoughtDate >= dayStart && thoughtDate <= dayEnd;
      } catch (error) {
        console.error("Error parsing thought timestamp:", error);
        return false;
      }
    });
    
    // Sort by time (no need to assign to a variable since we aren't using it)
    filteredThoughts.sort((a, b) => {
      try {
        return new Date(a.timestamp) - new Date(b.timestamp);
      } catch (error) {
        return 0;
      }
    });
  }, [thoughts, currentDate]);

  // Navigate to previous week
  const handlePrevWeek = () => {
    const prevWeek = new Date(currentDate);
    prevWeek.setDate(prevWeek.getDate() - 7);
    setCurrentDate(prevWeek);
  };

  // Navigate to next week
  const handleNextWeek = () => {
    const nextWeek = new Date(currentDate);
    nextWeek.setDate(nextWeek.getDate() + 7);
    setCurrentDate(nextWeek);
  };

  // Handle today button click
  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Navigate to thought detail
  const handleThoughtClick = (thoughtId) => {
    if (thoughtId) {
      navigate(`/thought/${thoughtId}`);
    }
  };

  // Format time from timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return "--:--";
    
    try {
      return new Date(timestamp).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "--:--";
    }
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  // Check if two dates are the same day
  const isSameDay = (date1, date2) => {
    return date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear();
  };

  // Select a date from the date selector
  const handleDateSelect = (date) => {
    setCurrentDate(new Date(date));
  };

  // Get thoughts for a specific day
  const getThoughtsForDate = (date) => {
    if (!thoughts || !thoughts.length) return [];
    
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);
    
    // Filter thoughts for this day and only include milestone thoughts
    const filteredThoughts = thoughts.filter(thought => {
      if (!thought || !thought.timestamp) return false;
      
      // Check if thought has milestone tag
      const hasMilestoneTag = thought.tags && 
        Array.isArray(thought.tags) && 
        thought.tags.some(tag => tag.toLowerCase() === 'milestone');
      
      if (!hasMilestoneTag) return false;
      
      try {
        const thoughtDate = new Date(thought.timestamp);
        return thoughtDate >= dayStart && thoughtDate <= dayEnd;
      } catch (error) {
        return false;
      }
    });
    
    return filteredThoughts.sort((a, b) => {
      try {
        return new Date(a.timestamp) - new Date(b.timestamp);
      } catch (error) {
        return 0;
      }
    });
  };
  
  // Get previous day date
  const getPrevDay = () => {
    const prevDay = new Date(currentDate);
    prevDay.setDate(prevDay.getDate() - 1);
    return prevDay;
  };
  
  // Get next day date
  const getNextDay = () => {
    const nextDay = new Date(currentDate);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay;
  };

  // Render hour thoughts
  const renderHourSlots = (date) => {
    const thoughts = getThoughtsForDate(date);
    
    return (
      <>
        {/* Thought count for the day */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          fontSize: '14px',
          fontWeight: '500',
          color: colors.textSecondary,
        }}>
          {thoughts.length} thought{thoughts.length !== 1 ? 's' : ''}
        </div>
        
        {/* Hour slots */}
        {Array.from({ length: 24 }).map((_, hour) => {
          // Find thoughts for this hour
          const hourThoughts = thoughts.filter(thought => {
            if (!thought || !thought.timestamp) return false;
            
            try {
              const thoughtDate = new Date(thought.timestamp);
              return thoughtDate.getHours() === hour;
            } catch (error) {
              return false;
            }
          });
          
          const hasThoughts = hourThoughts.length > 0;
          
          return (
            <div key={`${date.toISOString()}-${hour}`} style={{
              display: 'flex',
              flexDirection: 'column',
              borderBottom: hour < 23 ? `1px solid ${colors.divider}` : 'none',
              paddingBottom: hasThoughts ? '10px' : '8px',
              opacity: hasThoughts ? 1 : 0.6
            }}>
              {/* Hour label - only show if there are thoughts */}
              {hasThoughts && (
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: colors.textSecondary,
                  marginBottom: '8px'
                }}>
                  {hour === 0 ? '12 AM' : 
                    hour < 12 ? `${hour} AM` : 
                    hour === 12 ? '12 PM' : 
                    `${hour - 12} PM`}
                </div>
              )}
              
              {/* Thoughts for this hour */}
              {hourThoughts.map((thought) => (
                <div
                  key={thought.id}
                  onClick={() => handleThoughtClick(thought.id)}
                  style={{
                    padding: '10px 12px',
                    marginBottom: '8px',
                    marginLeft: '20px',
                    backgroundColor: colors.background,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    borderLeft: `3px solid ${colors.accent}`
                  }}
                >
                  <div style={{
                    color: colors.textSecondary,
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {formatTime(thought.timestamp)}
                  </div>
                  <div style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical'
                  }}>
                    {thought.thought ? 
                      thought.thought :
                      'No content'
                    }
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </>
    );
  };

  // Effect to clean up transitions on unmount
  useEffect(() => {
    return () => {
      // Ensure all transitions are cleared when component unmounts
      document.querySelectorAll('.calendar-page').forEach(el => {
        if (el) el.style.transition = 'none';
      });
    };
  }, []);

  // Set loading to false after a short delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Trigger fade in animation after component mounts and loading is done
  useEffect(() => {
    if (!isLoading) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      padding: '8px 8px', 
      paddingTop: '80px',
      backgroundColor: colors.background,
      color: colors.textPrimary,
      opacity: isVisible ? 1 : 0,
      transition: 'opacity 0.5s ease',
      willChange: 'opacity',
      maxWidth: '1000px',
      margin: '0 auto',
    }}
    className="page-content">
      {/* Global styles for transition */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes fadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }
          
          .calendar-content-wrapper {
            display: flex;
            flex-direction: column;
            flex: 1;
            overflow: hidden;
            position: relative;
            border-radius: 12px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            will-change: transform;
            -webkit-overflow-scrolling: touch;
            isolation: isolate;
            touch-action: none; /* Prevent browser handling of touch events */
          }
          .calendar-page {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: ${colors.cardBackground};
            padding: 16px;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            gap: 12px;
            will-change: transform, opacity;
            -webkit-overflow-scrolling: touch;
            touch-action: pan-y; /* Allow vertical scrolling within day pages */
          }
          .calendar-page.current {
            position: relative;
            opacity: 1;
            z-index: 1;
          }
          .calendar-page.prev {
            transform: translateX(-100%);
            opacity: ${swiping ? '1' : '0'};
            z-index: 0;
          }
          .calendar-page.next {
            transform: translateX(100%);
            opacity: ${swiping ? '1' : '0'};
            z-index: 0;
          }
        `}
      </style>
      
      {/* Date selector */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        marginTop: '8px',
        marginBottom: '12px',
        padding: '0 16px'
      }}>
        {/* Month display */}
        <div style={{
          fontSize: '14px',
          fontWeight: '400',
          color: colors.textPrimary,
        }}>
          {formatMonthYear(currentDate)}
        </div>

        {/* Today button */}
        <button 
          onClick={handleToday}
          style={{
            backgroundColor: '#FFFFFF',
            color: '#000000',
            border: 'none',
            borderRadius: '20px',
            padding: '8px 16px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          }}
        >
          Today
        </button>
      </div>
      
      {/* Week selector */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative'
      }}>
        <button 
          onClick={handlePrevWeek}
          style={{
            ...styles.button,
            background: 'transparent',
            color: colors.textSecondary,
            padding: '6px 8px',
            position: 'absolute',
            left: 0,
            zIndex: 5
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="3" strokeLinecap="butt" strokeLinejoin="miter"/>
          </svg>
        </button>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto',
          overflowX: 'auto',
          padding: '0 36px',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
          {weekDates.map((date, index) => {
            const isSelected = isSameDay(date, currentDate);
            const isTodayDate = isToday(date);
            
            return (
              <div 
                key={index}
                onClick={() => handleDateSelect(date)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '8px 0',
                  minWidth: '40px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  backgroundColor: isSelected ? colors.accentLight : 'transparent',
                  border: isTodayDate && !isSelected ? `1px solid ${colors.accent}` : 'none',
                }}
              >
                <div style={{
                  fontSize: '12px',
                  fontWeight: '500',
                  color: colors.textSecondary,
                  marginBottom: '4px',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: 'transparent'
                }}>
                  {formatDay(date)}
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: isTodayDate ? '800' : '400',
                  color: isSelected ? '#000000' : colors.textPrimary,
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: isSelected ? '#FFFFFF' : 'transparent',
                  boxShadow: isSelected ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                }}>
                  {formatDateShort(date)}
                </div>
              </div>
            );
          })}
        </div>
        
        <button 
          onClick={handleNextWeek}
          style={{
            ...styles.button,
            background: 'transparent',
            color: colors.textSecondary,
            padding: '6px 8px',
            position: 'absolute',
            right: 0,
            zIndex: 5
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="3" strokeLinecap="butt" strokeLinejoin="miter"/>
          </svg>
        </button>
      </div>
      
      {/* Swipe area with three panels (prev, current, next) */}
      <div 
        ref={containerRef}
        className="calendar-content-wrapper"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          touchAction: 'pan-y' // Allow vertical scrolling without interference
        }}
      >
        {/* Previous day content */}
        <div 
          ref={prevDayContentRef}
          className="calendar-page prev" 
          style={{
            transform: swiping ? `translateX(calc(${swipeOffset}px - 100%))` : 'translateX(-100%)',
            opacity: swiping && swipeOffset > 0 ? 1 : 0
          }}
        >
          {/* Previous day hour slots */}
          {renderHourSlots(getPrevDay())}
        </div>
        
        {/* Current day content */}
        <div 
          ref={contentRef}
          className="calendar-page current"
          style={{
            transform: swiping ? `translateX(${swipeOffset}px)` : 'translateX(0)'
          }}
        >
          {/* Current day hour slots */}
          {renderHourSlots(currentDate)}
        </div>
        
        {/* Next day content */}
        <div 
          ref={nextDayContentRef}
          className="calendar-page next"
          style={{
            transform: swiping ? `translateX(calc(100% + ${swipeOffset}px))` : 'translateX(100%)',
            opacity: swiping && swipeOffset < 0 ? 1 : 0
          }}
        >
          {/* Next day hour slots */}
          {renderHourSlots(getNextDay())}
        </div>
      </div>

      {/* CSS for iOS-like button press effect */}
      <style>
        {`
          .ios-button {
            -webkit-tap-highlight-color: transparent;
          }
          
          .ios-button:active {
            animation: ios-tap-pulse 180ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
          }
          
          .ios-tapped {
            animation: ios-tap-pulse 180ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
          }

          @keyframes ios-tap-pulse {
            0% { opacity: 1; transform: scale(1); }
            35% { opacity: 0.4; transform: scale(0.97); }
            100% { opacity: 1; transform: scale(1); }
          }
        `}
      </style>
    </div>
  );
};

export default TimelineView; 